import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Dots from '../../assets/images/Dots.svg';
import ActiveDots from '../../assets/images/active-dots.svg';
import gallery from '../../assets/images/gallery.png';
import { useOutsideClick } from '../../utils/helper';

const OfferingCard = ({
  offering,
  onEdit,
  onDelete,
  onManage,
  onPromoteToggle,
  actionMenuId,
  setActionMenuId
}) => {
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const [userType, setUserType] = useState(null);
  const [isPromoted, setIsPromoted] = useState(offering?.isPromoteToTop || false);
  const [isTogglingPromote, setIsTogglingPromote] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      setUserType(parsedData.userType);
    }
  }, []);

  useEffect(() => {
    setIsPromoted(offering?.isPromoteToTop || false);
  }, [offering?.isPromoteToTop]);

  const handlePromoteToggle = async (e) => {
    e.stopPropagation();
    if (isTogglingPromote || !onPromoteToggle) return;

    setIsTogglingPromote(true);
    try {
      await onPromoteToggle(offering.id);
      setIsPromoted(!isPromoted);
    } catch (error) {
      console.error('Error toggling promote:', error);
    } finally {
      setIsTogglingPromote(false);
    }
  };

  useOutsideClick(cardRef, () => {
    if (actionMenuId === offering?.id) setActionMenuId('');
  });

  return (
    <div className="w-full bg-white dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray rounded-2xl overflow-hidden">
      {/* Header with Image */}
      <div className="relative bg-[#F4F4F4] dark:bg-shoorah-gray6 rounded-t-2xl overflow-hidden">
        {offering.image ? (
          <img
            src={offering.image}
            alt={offering.title}
            className="object-cover w-full h-[160px]"
          />
        ) : (
          <div className="flex justify-center items-center h-[160px]">
            <img src={gallery} alt="offering placeholder" className="w-[48px] h-[48px]" />
          </div>
        )}

        {/* Promote to Top Toggle */}
        <div className="absolute top-3 right-3 bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-md px-3 py-2 flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            Promote to Top
          </span>
          <button
            onClick={handlePromoteToggle}
            disabled={isTogglingPromote}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
              isPromoted ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
            } ${isTogglingPromote ? 'opacity-50 cursor-not-allowed' : ''}`}
            role="switch"
            aria-checked={isPromoted}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isPromoted ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="h-fit flex flex-col lg:gap-4 gap-2 lg:px-4 px-3 lg:py-6 py-4">
        {/* Title and Action Menu */}
        <div className="flex justify-between items-start gap-2">
          <p className="lg:text-[20px] text-[16px] text-shoorah-lightBlack dark:text-shoorah-darkBg font-bold leading-6 truncate max-w-[80%]">
            {offering?.title}
          </p>
          <button
            ref={cardRef}
            className="relative flex-shrink-0"
            onClick={() => setActionMenuId(actionMenuId === offering?.id ? '' : offering?.id)}
          >
            <img src={Dots} alt="Menu icon" className="w-6 h-6 block dark:hidden" />
            <img src={ActiveDots} alt="Menu icon" className="w-6 h-6 hidden dark:block" />

            {/* Action Menu Dropdown */}
            {offering?.id === actionMenuId && (
              <div className="w-40 z-50 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder absolute top-4 right-0 mt-2 border-[1.5px] border-shoorah-gray1 rounded-lg shadow-lg py-[14px]">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(offering);
                    setActionMenuId('');
                  }}
                  className="py-2 px-5 hover:bg-shoorah-borderGray dark:hover:bg-shoorah-darkBgColor cursor-pointer font-medium leading-6 text-[16px] text-shoorah-lightBlack dark:text-shoorah-darkBg text-left w-full block"
                >
                  Edit
                </button>

                <div className="border-b border-gray-400 w-full"></div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onManage?.(offering);
                    setActionMenuId('');
                  }}
                  className="py-2 px-5 hover:bg-shoorah-borderGray dark:hover:bg-shoorah-darkBgColor cursor-pointer font-medium leading-6 text-[16px] text-shoorah-lightBlack dark:text-shoorah-darkBg text-left w-full block"
                >
                  Manage
                </button>

                <div className="border-b border-gray-400 w-full"></div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(offering);
                    setActionMenuId('');
                  }}
                  className="py-2 px-5 hover:bg-shoorah-borderGray dark:hover:bg-shoorah-darkBgColor cursor-pointer text-red-700 font-medium leading-6 text-[16px] text-left w-full block"
                >
                  Delete
                </button>
              </div>
            )}
          </button>
        </div>

        {/* Description */}
        <p className="lg:text-[16px] text-[14px] text-shoorah-lightBlack dark:text-shoorah-darkBg font-normal leading-5 line-clamp-2">
          {offering.description}
        </p>

        {/* Status Badge */}
        {offering.status && (
          <div className="mt-2">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                offering.status === 'approved'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : offering.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : offering.status === 'rejected'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
              }`}
            >
              {offering.status?.charAt(0).toUpperCase() + offering.status?.slice(1)}
            </span>
          </div>
        )}

        {/* Manage Link */}
        <button
          onClick={() => onManage?.(offering)}
          className="flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors mt-2"
        >
          <span>Manage</span>
          <ArrowRightIcon className="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default OfferingCard;
